import { useState } from "react";
import { Plus } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { api } from "../lib/api";

const VISIBILITY = [
  { id: "one_off", label: "This invoice only", hint: "Not saved to your products" },
  { id: "menu", label: "Save & show on menu", hint: "Customers can order it" },
  { id: "internal", label: "Save as internal only", hint: "Only you see it on invoices" },
];

const EMPTY = { name: "", price: "", category: "", description: "", visibility: "one_off" };

const VisibilityPicker = ({ value, onChange, testidPrefix }) => (
  <div className="sm:col-span-2">
    <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/60">
      Where should it live?
    </Label>
    <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-1.5" role="radiogroup">
      {VISIBILITY.map((v) => (
        <button
          key={v.id}
          type="button"
          role="radio"
          aria-checked={value === v.id}
          data-testid={`${testidPrefix}-visibility-${v.id}`}
          onClick={() => onChange(v.id)}
          className={`text-left rounded-xl border px-3 py-2 transition-colors ${
            value === v.id ? "border-brand-teal bg-brand-teal/10" : "border-black/10 bg-white hover:border-brand-teal/40"
          }`}
        >
          <span className="block text-[11px] font-bold uppercase tracking-[0.08em] text-brand-charcoal">{v.label}</span>
          <span className="block text-[10.5px] text-brand-charcoal/55">{v.hint}</span>
        </button>
      ))}
    </div>
  </div>
);

// Inline "create product" form used inside invoice builders.
export const NewProductForm = ({ categories = [], onCreated, onCancel, testidPrefix = "new-product" }) => {
  const [p, setP] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setP((s) => ({ ...s, [k]: e.target ? e.target.value : e }));
  const listId = `${testidPrefix}-categories`;

  const submit = async () => {
    setError("");
    const name = p.name.trim();
    const category = p.category.trim() || "Add-ons";
    const price = Number(p.price);
    if (!name) return setError("Product name is required.");
    if (p.price === "" || !Number.isFinite(price) || price < 0) return setError("Enter a price of 0 or more.");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/menu", {
        name,
        category,
        price,
        description: p.description.trim(),
        is_published: p.visibility === "menu",
        one_off: p.visibility === "one_off",
      });
      onCreated(data.item);
      setP(EMPTY);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : d?.[0]?.msg || "Could not create product.");
    } finally {
      setBusy(false);
    }
  };

  const field = "mt-1 h-10 rounded-xl bg-brand-cream/30 border-black/10";
  return (
    <div data-testid={`${testidPrefix}-form`} className="mt-2 rounded-xl bg-white border border-brand-teal/40 p-3 space-y-2">
      <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-teal">New product</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/60">Name *</Label>
          <Input data-testid={`${testidPrefix}-name`} value={p.name} onChange={set("name")} placeholder="e.g. Boxed Lunch — Turkey" className={field} />
        </div>
        <div>
          <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/60">Price *</Label>
          <Input data-testid={`${testidPrefix}-price`} type="number" step="0.01" min="0" value={p.price} onChange={set("price")} placeholder="12.50" className={field} />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/60">Category</Label>
          <Input list={listId} data-testid={`${testidPrefix}-category`} value={p.category} onChange={set("category")} placeholder="Pick or type — defaults to Add-ons" className={field} />
          <datalist id={listId}>
            {categories.map((c) => <option key={c} value={c} />)}
          </datalist>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/60">Description</Label>
          <Textarea data-testid={`${testidPrefix}-description`} value={p.description} onChange={set("description")} rows={2} placeholder="Optional — what's included." className="mt-1 rounded-xl bg-brand-cream/30 border-black/10" />
        </div>
        <VisibilityPicker value={p.visibility} onChange={set("visibility")} testidPrefix={testidPrefix} />
      </div>
      {error && (
        <div data-testid={`${testidPrefix}-error`} className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-[11.5px] text-red-700">
          {error}
        </div>
      )}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className="rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/60 hover:text-brand-charcoal">
          Cancel
        </button>
        <button
          type="button"
          data-testid={`${testidPrefix}-submit`}
          onClick={submit}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-black/85 transition-colors disabled:opacity-50"
        >
          <Plus className="h-3 w-3" />
          {busy ? "Creating…" : "Create + Add"}
        </button>
      </div>
    </div>
  );
};

export default NewProductForm;
